import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import type { CloneSummary, Health } from '@api/api';
import { HealthDot, EmptyState } from './common';
import { daysUntil, fmtAgeHours, fmtMoney, fmtNumber, fmtDate } from '../lib/format';
import { HEALTH_ORDER } from '../lib/status';

type SortKey = 'health' | 'name' | 'release' | 'errors' | 'backup' | 'tls' | 'rides' | 'revenue' | 'alerts' | 'renewal';
export type HealthFilter = 'all' | 'problems' | Health;

const COLS: { key: SortKey; label: string; align?: 'right' }[] = [
  { key: 'health', label: '' },
  { key: 'name', label: 'Clone' },
  { key: 'release', label: 'Release' },
  { key: 'errors', label: 'Errors 5m', align: 'right' },
  { key: 'backup', label: 'Backup', align: 'right' },
  { key: 'tls', label: 'TLS', align: 'right' },
  { key: 'rides', label: 'Rides today', align: 'right' },
  { key: 'revenue', label: 'Revenue 30d', align: 'right' },
  { key: 'alerts', label: 'Alerts', align: 'right' },
  { key: 'renewal', label: 'Licence' },
];

function sortValue(c: CloneSummary, k: SortKey): number | string {
  switch (k) {
    case 'health':
      return HEALTH_ORDER[c.health];
    case 'name':
      return c.name.toLowerCase();
    case 'release':
      return c.release ?? '';
    case 'errors':
      return c.errors5m ?? -1;
    case 'backup':
      return c.backupAgeHours ?? -1;
    case 'tls':
      return c.tlsDaysLeft ?? 9999;
    case 'rides':
      return c.stats?.rides.today ?? -1;
    case 'revenue':
      return c.stats?.revenue.d30 ?? -1;
    case 'alerts':
      return c.openAlerts.critical * 1000 + c.openAlerts.warning;
    case 'renewal':
      return c.renewalDate ?? '9999';
  }
}

export function filterByHealth(clones: CloneSummary[], f: HealthFilter): CloneSummary[] {
  if (f === 'all') return clones;
  if (f === 'problems') return clones.filter((c) => c.health === 'down' || c.health === 'degraded' || c.openAlerts.critical + c.openAlerts.warning > 0);
  return clones.filter((c) => c.health === f);
}

const tone = (bad: boolean, warn: boolean) => (bad ? 'error.main' : warn ? 'warning.main' : undefined);

export function RenewalCell({ c, now = new Date() }: { c: CloneSummary; now?: Date }) {
  if (!c.licence) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const days = daysUntil(c.renewalDate, now);
  const soon = days !== null && days <= 14;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box>
        <Typography variant="body2">
          {c.licence === 'rent' ? 'Rent' : 'Sale'}
          {c.monthlyFee !== null && ` · ${fmtMoney(c.monthlyFee, c.feeCurrency)}/mo`}
        </Typography>
        {c.renewalDate && (
          <Typography variant="caption" color="text.secondary">
            renews {fmtDate(c.renewalDate)}
          </Typography>
        )}
      </Box>
      {soon && (
        <Chip
          size="small"
          color={days! < 0 ? 'error' : 'warning'}
          label={days! < 0 ? `overdue ${-days!} d` : days === 0 ? 'today' : `in ${days} d`}
          data-testid="renewal-soon"
        />
      )}
    </Stack>
  );
}

export function CloneTable({ clones, filter: filterProp, onFilterChange, dense }: { clones: CloneSummary[]; filter?: HealthFilter; onFilterChange?: (f: HealthFilter) => void; dense?: boolean }) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'health', dir: 'asc' });
  const [localFilter, setLocalFilter] = useState<HealthFilter>('all');
  const filter = filterProp ?? localFilter;
  const setFilter = onFilterChange ?? setLocalFilter;

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: clones.length, problems: filterByHealth(clones, 'problems').length };
    for (const x of clones) c[x.health] = (c[x.health] ?? 0) + 1;
    return c;
  }, [clones]);

  const rows = useMemo(() => {
    const list = filterByHealth(clones, filter);
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const va = sortValue(a, sort.key);
      const vb = sortValue(b, sort.key);
      const r = va < vb ? -1 : va > vb ? 1 : 0;
      return r * dir || a.name.localeCompare(b.name);
    });
  }, [clones, filter, sort]);

  const toggle = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' || key === 'health' || key === 'tls' || key === 'renewal' ? 'asc' : 'desc' }));

  const filters: [HealthFilter, string][] = [
    ['all', 'All'],
    ['problems', 'Needs attention'],
    ['ok', 'OK'],
    ['degraded', 'Degraded'],
    ['down', 'Down'],
    ['provisioning', 'Provisioning'],
    ['unknown', 'Draft / unknown'],
  ];

  return (
    <Box>
      <Box sx={{ overflowX: 'auto', mb: 1.5 }}>
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)} aria-label="Filter by health">
          {filters
            .filter(([k]) => k === 'all' || k === 'problems' || counts[k])
            .map(([k, label]) => (
              <ToggleButton key={k} value={k} sx={{ px: 1.5, whiteSpace: 'nowrap' }}>
                {label}
                <Box component="span" sx={{ ml: 0.75, color: 'text.secondary', fontWeight: 400 }}>
                  {counts[k] ?? 0}
                </Box>
              </ToggleButton>
            ))}
        </ToggleButtonGroup>
      </Box>
      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
        <Table size="small" aria-label="Clones" stickyHeader={!dense}>
          <TableHead>
            <TableRow>
              {COLS.map((col, i) => (
                <TableCell
                  key={col.key}
                  align={col.align}
                  sortDirection={sort.key === col.key ? sort.dir : false}
                  sx={i < 2 ? { position: 'sticky', left: i === 0 ? 0 : 36, zIndex: 3, bgcolor: 'background.paper', width: i === 0 ? 36 : undefined, pr: i === 0 ? 0 : undefined } : undefined}
                >
                  <TableSortLabel active={sort.key === col.key} direction={sort.key === col.key ? sort.dir : 'asc'} onClick={() => toggle(col.key)}>
                    {col.label || (
                      <Box component="span" sx={visuallyHidden}>
                        Health
                      </Box>
                    )}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((c) => {
              const s = c.stats;
              return (
                <TableRow key={c.id} hover sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }} onClick={() => navigate(`/clones/${c.id}`)} data-testid={`clone-row-${c.slug}`}>
                  <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, pr: 0 }}>
                    <HealthDot health={c.health} />
                  </TableCell>
                  <TableCell sx={{ position: 'sticky', left: 36, bgcolor: 'background.paper', zIndex: 1, maxWidth: 260 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {c.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {c.clientName} ·{' '}
                      <Link href={`https://${c.domain}`} target="_blank" rel="noreferrer" underline="hover" onClick={(e) => e.stopPropagation()}>
                        {c.domain}
                      </Link>
                    </Typography>
                  </TableCell>
                  <TableCell>{c.release ?? <Typography variant="body2" color="text.secondary">—</Typography>}</TableCell>
                  <TableCell align="right" sx={{ color: tone((c.errors5m ?? 0) >= 20, (c.errors5m ?? 0) > 0), fontVariantNumeric: 'tabular-nums' }}>
                    {fmtNumber(c.errors5m)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: tone((c.backupAgeHours ?? 0) > 50, (c.backupAgeHours ?? 0) > 26) }}>
                    {fmtAgeHours(c.backupAgeHours)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: c.tlsDaysLeft === null ? undefined : tone(c.tlsDaysLeft < 3, c.tlsDaysLeft < 14) }}>
                    {c.tlsDaysLeft === null ? '—' : `${c.tlsDaysLeft} d`}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmtNumber(s?.rides.today)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {s ? fmtMoney(s.revenue.d30, s.currency) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {c.openAlerts.critical + c.openAlerts.warning === 0 ? (
                      <Typography variant="body2" color="text.secondary">0</Typography>
                    ) : (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {c.openAlerts.critical > 0 && (
                          <Tooltip title="Critical">
                            <Chip size="small" color="error" label={c.openAlerts.critical} />
                          </Tooltip>
                        )}
                        {c.openAlerts.warning > 0 && (
                          <Tooltip title="Warning">
                            <Chip size="small" color="warning" label={c.openAlerts.warning} />
                          </Tooltip>
                        )}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell>
                    <RenewalCell c={c} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {rows.length === 0 && <EmptyState title={clones.length ? 'Nothing matches this filter' : 'No clones yet'} text={clones.length ? undefined : 'Create the first installation for a client with “New clone”.'} />}
      </TableContainer>
    </Box>
  );
}
